/**
 * Klaviyo product reviews: precise average stars + lightbox backdrop close.
 * Also collapses the theme section when the widget never loads (e.g. ad blockers).
 */
(() => {
  const ROOT_SEL = '#klaviyo-product-reviews-wrapper';
  const LIGHTBOX_SEL = '.kl_reviews__lightbox_background';
  const LIGHTBOX_CHROME_SEL =
    '.kl_reviews__lightbox__content_wrapper, .kl_reviews__lightbox__image_list, .kl_reviews__close_button';
  const CONTENT_SEL =
    '.kl_reviews__summary, .kl_reviews__review, .kl_reviews__list, .kl_reviews__carousel';
  const HIDDEN_ATTR = 'data-antic-reviews-empty';
  const EMPTY_WAIT_MS = 2500;
  const STAR_PATH =
    'M31.547 12a.848.848 0 00-.677-.577l-9.427-1.376-4.224-8.532a.847.847 0 00-1.516 0l-4.218 8.534-9.427 1.355a.847.847 0 00-.467 1.467l6.823 6.664-1.612 9.375a.847.847 0 001.23.893l8.428-4.434 8.432 4.432a.847.847 0 001.229-.894l-1.615-9.373 6.822-6.665a.845.845 0 00.214-.869z';

  function sectionFor(root) {
    return root.closest('.shopify-section');
  }

  function hasReviewsContent(root) {
    return Boolean(root.querySelector(CONTENT_SEL));
  }

  function setSectionEmpty(section, empty) {
    if (!section) return;
    if (empty) section.setAttribute(HIDDEN_ATTR, '');
    else section.removeAttribute(HIDDEN_ATTR);
  }

  function syncEmptyState(root) {
    setSectionEmpty(sectionFor(root), !hasReviewsContent(root));
  }

  function parseAverage(root) {
    const el = root.querySelector('.kl_reviews__summary__average_value');
    if (!el) return null;
    const avg = parseFloat(String(el.textContent || '').replace(/[^\d.]/g, ''));
    return Number.isFinite(avg) && avg > 0 ? avg : null;
  }

  function findSummaryStarHost(root) {
    const summary = root.querySelector('.kl_reviews__summary__average_and_stars');
    if (!summary) return null;

    const badge = summary.querySelector('.kl_reviews__stars_badge');
    if (badge) return badge;

    const firstStar = summary.querySelector(
      '.kl_reviews__full_star, .kl_reviews__partial_star, .kl_reviews__empty_star, .kl_reviews__star'
    );
    return firstStar?.parentElement || summary;
  }

  function hideNativeSummaryStars(host) {
    host
      .querySelectorAll(
        '.kl_reviews__full_star, .kl_reviews__partial_star, .kl_reviews__empty_star, .kl_reviews__star'
      )
      .forEach((el) => {
        if (el.closest('.kl_reviews__review')) return;
        el.setAttribute('hidden', '');
        el.style.setProperty('display', 'none', 'important');
      });
  }

  function buildStarsMarkup(avg) {
    const full = Math.floor(avg);
    const fraction = avg - full;
    const partialPct = Math.round(fraction * 1000) / 10;
    const uid = `antic-kl-star-${Math.round(avg * 1000)}`;

    let html = '';

    for (let i = 1; i <= 5; i += 1) {
      const clsBase = 'antic-kl-precise-stars__star';
      if (i <= full) {
        html += `<svg class="${clsBase} ${clsBase}--full" viewBox="0 0 32 32" width="18" height="18" aria-hidden="true" focusable="false"><path d="${STAR_PATH}" fill="currentColor"></path></svg>`;
      } else if (i === full + 1 && fraction > 0) {
        const clipWidth = (partialPct / 100) * 32;
        html += `<svg class="${clsBase} ${clsBase}--partial" viewBox="0 0 32 32" width="18" height="18" aria-hidden="true" focusable="false"><defs><clipPath id="${uid}-clip" clipPathUnits="userSpaceOnUse"><rect x="0" y="0" width="${clipWidth}" height="32"></rect></clipPath></defs><path d="${STAR_PATH}" fill="rgba(249,236,215,0.22)"></path><path d="${STAR_PATH}" fill="currentColor" clip-path="url(#${uid}-clip)"></path></svg>`;
      } else {
        html += `<svg class="${clsBase} ${clsBase}--empty" viewBox="0 0 32 32" width="18" height="18" aria-hidden="true" focusable="false"><path d="${STAR_PATH}" fill="rgba(249,236,215,0.22)"></path></svg>`;
      }
    }

    return html;
  }

  function openSummaryLightbox(root) {
    const first = root.querySelector(
      '[class*="kl_reviews__summary__image_container"] .kl_reviews__summary_image:not([data-antic-cloned])'
    );
    if (first instanceof HTMLElement) first.click();
  }

  function photoContainer(root) {
    return (
      root.querySelector('.kl_reviews__summary__image_container--large_grid') ||
      root.querySelector('.kl_reviews__summary__image_container--small_grid') ||
      root.querySelector('[class*="summary__image_container"]')
    );
  }

  function imageKey(src) {
    const match = String(src || '').match(/UBBAGk\/[^/?]+/);
    return match ? match[0] : String(src || '');
  }

  /* Klaviyo’s React tree only mounts 3 thumbs (`small_grid`) and wipes extra
     nodes on re-render. Keep URLs in JS so we can put 4–6 back after each paint. */
  const photoCache = [];
  let harvestAttempted = false;

  function rememberPhoto(src, alt) {
    const key = imageKey(src);
    if (!key || photoCache.some((item) => item.key === key)) return;
    photoCache.push({ key, src, alt: alt || '' });
  }

  function collectPhotos(root) {
    root
      .querySelectorAll(
        '.kl_reviews__summary_image img, .kl_reviews__review__image_container img'
      )
      .forEach((img) => rememberPhoto(img.currentSrc || img.src, img.alt));

    document
      .querySelectorAll(
        '.kl_reviews__lightbox img, .kl_reviews__gallery_lightbox_thumbnail_container img'
      )
      .forEach((img) => rememberPhoto(img.currentSrc || img.src, img.alt));
  }

  function fillSummaryPhotos(root) {
    collectPhotos(root);
    const photos = photoContainer(root);
    if (!photos) return;

    photos.classList.add('antic-kl-photo-strip');

    const existing = new Set(
      [...photos.querySelectorAll('.kl_reviews__summary_image img')]
        .map((img) => imageKey(img.currentSrc || img.src))
        .filter(Boolean)
    );

    const viewAll = photos.querySelector('.antic-kl-view-all');

    photoCache.forEach((item) => {
      if (photos.querySelectorAll('.kl_reviews__summary_image').length >= 6) return;
      if (existing.has(item.key)) return;
      existing.add(item.key);

      const clone = document.createElement('div');
      clone.className = 'kl_reviews__summary_image';
      clone.dataset.anticCloned = 'true';
      clone.setAttribute('role', 'button');
      clone.tabIndex = 0;

      const img = document.createElement('img');
      img.className = 'kl_reviews__summary__image_photo';
      img.src = item.src;
      img.alt = item.alt;
      clone.appendChild(img);

      clone.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        openSummaryLightbox(root);
      });

      if (viewAll) photos.insertBefore(clone, viewAll);
      else photos.appendChild(clone);
    });
  }

  function endHarvest() {
    document.documentElement.removeAttribute('data-antic-kl-harvest');
  }

  function closeHarvestLightbox(root) {
    collectPhotos(root);
    document.querySelector(`${LIGHTBOX_SEL} .kl_reviews__close_button`)?.click();
    fillSummaryPhotos(root);
    layoutSummaryPhotos(root);

    /* Keep harvest hide on until the lightbox is actually gone — removing the
       flag immediately after close() was flashing the modal on Bike PDP load. */
    const deadline = Date.now() + 2000;
    const finish = () => {
      if (!document.querySelector(LIGHTBOX_SEL) || Date.now() >= deadline) {
        endHarvest();
        return;
      }
      window.requestAnimationFrame(finish);
    };
    window.requestAnimationFrame(finish);
  }

  function harvestLightboxPhotos(root) {
    if (window.matchMedia('(max-width: 749px)').matches) return;
    if (photoCache.length >= 6 || harvestAttempted) return;

    const photos = photoContainer(root);
    const first = photos && photos.querySelector('.kl_reviews__summary_image:not([data-antic-cloned])');
    if (!(first instanceof HTMLElement)) return;

    harvestAttempted = true;
    document.documentElement.setAttribute('data-antic-kl-harvest', '');

    /* Ensure hide CSS is applied before Klaviyo paints the modal */
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (!document.documentElement.hasAttribute('data-antic-kl-harvest')) return;
        first.click();
        window.setTimeout(() => closeHarvestLightbox(root), 500);
      });
    });
  }

  function layoutSummaryPhotos(root) {
    fillSummaryPhotos(root);

    const photos = photoContainer(root);
    if (!photos) return;

    const images = [...photos.querySelectorAll('.kl_reviews__summary_image')];
    if (!images.length) return;

    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const tabletSm = window.matchMedia('(max-width: 989px)').matches;
    const tablet = window.matchMedia('(max-width: 1199px)').matches;
    const wide = window.matchMedia('(min-width: 1400px)').matches;

    photos.style.setProperty('display', 'flex', 'important');
    photos.style.setProperty('flex-direction', 'row', 'important');
    photos.style.setProperty('flex-wrap', 'nowrap', 'important');
    photos.style.setProperty('grid-template-columns', 'none', 'important');
    photos.style.setProperty('grid-auto-flow', 'column', 'important');
    photos.style.setProperty('height', 'auto', 'important');
    photos.style.setProperty('width', mobile ? '100%' : 'max-content', 'important');
    photos.style.setProperty('max-width', mobile ? '100%' : 'none', 'important');

    let size = '120px';
    let visible = 6;
    if (mobile) {
      size = '';
      visible = 3;
    } else if (tabletSm) {
      size = '84px';
      visible = 4;
    } else if (tablet) {
      size = '96px';
      visible = 5;
    } else if (wide) {
      size = '150px';
    }

    images.forEach((img, index) => {
      const hide = index >= visible;
      img.style.setProperty('display', hide ? 'none' : 'block', 'important');
      if (hide) return;
      if (mobile) {
        img.style.setProperty('flex', '1 1 0', 'important');
        img.style.setProperty('width', 'auto', 'important');
        img.style.setProperty('min-width', '0', 'important');
        img.style.setProperty('max-width', 'none', 'important');
        img.style.setProperty('height', 'auto', 'important');
        img.style.setProperty('max-height', 'none', 'important');
        return;
      }
      img.style.setProperty('flex', `0 0 ${size}`, 'important');
      img.style.setProperty('width', size, 'important');
      img.style.setProperty('min-width', size, 'important');
      img.style.setProperty('max-width', size, 'important');
      img.style.setProperty('height', size, 'important');
      img.style.setProperty('max-height', size, 'important');
    });
  }

  function ensureViewAll(root) {
    layoutSummaryPhotos(root);
    harvestLightboxPhotos(root);
    const photos = photoContainer(root);
    const first = photos && photos.querySelector('.kl_reviews__summary_image');
    if (!photos || !first) return;

    let btn = root.querySelector('.antic-kl-view-all');
    if (!btn) {
      btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'antic-kl-view-all';
      btn.textContent = 'View all';
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        openSummaryLightbox(root);
      });
    }
    if (btn.parentElement !== photos) photos.appendChild(btn);
  }

  function enhance(root) {
    ensureViewAll(root);

    const avg = parseAverage(root);
    if (avg == null) return false;

    const host = findSummaryStarHost(root);
    if (!host) return false;

    hideNativeSummaryStars(host);

    let mount = host.querySelector('[data-antic-precise-stars]');
    if (!mount) {
      mount = document.createElement('span');
      mount.dataset.anticPreciseStars = '';
      mount.className = 'antic-kl-precise-stars';
      mount.setAttribute('role', 'img');
      mount.setAttribute('aria-label', `${avg} out of 5 stars`);

      const count = host.querySelector('.kl_reviews__summary__stars__count');
      if (count) host.insertBefore(mount, count);
      else host.prepend(mount);
    }

    const key = String(avg);
    if (mount.dataset.avg === key) return true;
    mount.dataset.avg = key;
    mount.innerHTML = buildStarsMarkup(avg);
    return true;
  }

  function nestLightboxThumbs() {
    document.querySelectorAll(LIGHTBOX_SEL).forEach((overlay) => {
      const wrap = overlay.querySelector('.kl_reviews__lightbox__content_wrapper');
      const list = overlay.querySelector('.kl_reviews__lightbox__image_list');
      if (!wrap || !list || list.parentElement === wrap) return;
      wrap.appendChild(list);
    });

    document.querySelectorAll(`${LIGHTBOX_SEL} .kl_reviews__lightbox__image_list .swiper`).forEach((el) => {
      const swiper = el.swiper;
      if (!swiper || !swiper.params) return;
      swiper.params.slidesPerView = 'auto';
      swiper.params.spaceBetween = 8;
      if (typeof swiper.update === 'function') swiper.update();
    });
  }

  function scan() {
    document.querySelectorAll(ROOT_SEL).forEach((root) => {
      enhance(root);
      if (hasReviewsContent(root)) syncEmptyState(root);
    });
    nestLightboxThumbs();
  }

  function markEmptyIfStillMissing() {
    document.querySelectorAll(ROOT_SEL).forEach((root) => {
      syncEmptyState(root);
    });

    // App block shell with no hydrated wrapper (script fully blocked)
    document.querySelectorAll('[id*="klaviyo_reviews_product_reviews"]').forEach((shell) => {
      const section = shell.closest('.shopify-section');
      if (!section) return;
      const root = section.querySelector(ROOT_SEL);
      if (root && hasReviewsContent(root)) {
        setSectionEmpty(section, false);
        return;
      }
      if (!root || !hasReviewsContent(root)) setSectionEmpty(section, true);
    });
  }

  const observer = new MutationObserver(() => {
    scan();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan, { once: true });
  } else {
    scan();
  }

  observer.observe(document.documentElement, { childList: true, subtree: true });
  window.setTimeout(markEmptyIfStillMissing, EMPTY_WAIT_MS);
  window.addEventListener('resize', () => {
    document.querySelectorAll(ROOT_SEL).forEach(layoutSummaryPhotos);
  });

  document.addEventListener(
    'click',
    (event) => {
      const overlay = event.target instanceof Element ? event.target.closest(LIGHTBOX_SEL) : null;
      if (!overlay) return;
      if (event.target.closest(LIGHTBOX_CHROME_SEL)) return;

      overlay.querySelector('.kl_reviews__close_button')?.click();
    },
    true
  );
})();
